import { createRouter, createWebHistory, createWebHashHistory } from "vue-router";
import { isDesktop, getTenant, getRouteBase } from "./services/tenant.js";
import { LIST_TYPES } from "./features/smart-routing/api.js";
import { SETTINGS_SECTIONS } from "./pages/settings/sections.js";
import store from "./store/index.js";
import HomePage from "./pages/HomePage.vue";
import LoginPage from "./pages/LoginPage.vue";
import ProfilePage from "./pages/ProfilePage.vue";
import NotFound from "./pages/NotFound.vue";
import ForgotPasswordPage from "./pages/ForgotPasswordPage.vue";
import CreateUserPage from "./pages/admin/users/CreateUser.vue";
import ManageSkill from "./pages/admin/skills/ManageSkill.vue";
import CallsReport from "./pages/reports/CallsReport.vue";
import PerformanceByPeriodReport from "./pages/reports/PerformanceByPeriod.vue";
import TotalPerformanceReport from "./pages/reports/TotalPerformance.vue";
import AgentPerformanceReport from "./pages/reports/AgentsPerformance.vue";
import AgentCallsReport from "./pages/agent/reports/AgentCalls.vue";
import AgentStatusReport from "./pages/agent/reports/AgentStatus.vue";
import SystemInboundReport from "./pages/reports/SystemInbound.vue";
import AgentLeavingDetailReport from "./pages/reports/AgentLeavingDetail.vue";
import AgentLeavingSummaryReport from "./pages/reports/AgentLeavingSummary.vue";
import AgentLoginDetailReport from "./pages/reports/AgentLoginDetail.vue";
import AgentLoginSummaryReport from "./pages/reports/AgentLoginSummary.vue";
import AgentStatusDetailReport from "./pages/reports/AgentStatusDetail.vue";
import AgentStatusSummaryReport from "./pages/reports/AgentStatusSummary.vue";
import AgentKPIReport from "./pages/reports/KPIReport.vue";

import ChatPage from "./pages/chat/MainChat.vue";
// import SMSPage from "./pages/sms/SMSPage.vue";
import SocialMediaPage from "./pages/social-media/SocialMedia.vue";
import AiAssistantPage from "./pages/AiAssistantPage.vue"; // AI Assistant disabled
import Reports from "./pages/reports/ReportMain.vue";
import CallAnalysisDetails from "./pages/reports/CallAnalysisDetails.vue";
import VoiceCDRDetails from "./pages/reports/VoiceCDRDetails.vue";
import AIChatDetails from "./pages/reports/AIChatDetails.vue";

function baseURL() {
  return getRouteBase();
}
const reportsMeta = { requireAuth: true, permission: "I" };
// AI report detail pages share the reports permission until the backend
// exposes a dedicated one.
const aiReportsMeta = { requireAuth: true, permission: "I" };
const router = createRouter({
  history: isDesktop ? createWebHashHistory() : createWebHistory(),
  base: "call-center",
  routes: [
    {
      path: baseURL() + "/",
      alias: baseURL() + "/Home",
      name: "Home",
      component: HomePage,
      meta: { requireAuth: true, permission: "A" },
    },
    {
      path: baseURL() + "/Login",
      name: "Login",
      component: LoginPage,
      meta: { requireAuth: false },
    },
    {
      path: baseURL() + "/forgot-password",
      name: "forgot-password",
      component: ForgotPasswordPage,
      meta: { requireAuth: false },
    },
    {
      path: baseURL() + "/Home",
      name: "Home",
      component: HomePage,
      meta: { requireAuth: true, permission: "A" },
    },
    {
      path: baseURL() + "/Profile",
      name: "Profile",
      component: ProfilePage,
      meta: { requireAuth: true, permission: "A" },
    },
    {
      path: baseURL() + "/Chat",
      name: "chat",
      component: ChatPage,
      meta: { requireAuth: true, permission: "A" },
    },
    {
      path: baseURL() + "/Chat/:user",
      name: "chat-user",
      component: ChatPage,
      meta: { requireAuth: true, permission: "A" },
    },
    {
      path: baseURL() + "/social-media",
      name: "social-media",
      component: SocialMediaPage,
      meta: { requireAuth: true, permission: "A" },
    },
    {
      path: baseURL() + "/ai-assistant",
      name: "ai-assistant",
      component: AiAssistantPage,
      meta: { requireAuth: true, permission: "AD" },
    },
    // Old admin pages now live in the settings console; keep the old URLs
    // working via redirects.
    {
      path: baseURL() + "/Users",
      redirect: { name: "settings-users" },
    },
    {
      path: baseURL() + "/Users/Create",
      name: "create-user",
      component: CreateUserPage,
      meta: { requireAuth: true, permission: "J" },
    },
    {
      path: baseURL() + "/Skills",
      redirect: { name: "settings-queues" },
    },
    {
      path: baseURL() + "/Skills/:skill",
      name: "manage-skill",
      component: ManageSkill,
      meta: { requireAuth: true, permission: "J" },
    },
    {
      path: baseURL() + "/Calls",
      redirect: { name: "settings-reasons" },
    },
    {
      path: baseURL() + "/Reports",
      name: "Reports",
      component: Reports,
      meta: reportsMeta,
    },
    {
      path: baseURL() + "/Reports/CallsReport",
      name: "call-report",
      component: CallsReport,
      meta: reportsMeta,
    },
    {
      path: baseURL() + "/Reports/PerformanceByPeriod",
      name: "period-report",
      component: PerformanceByPeriodReport,
      meta: reportsMeta,
    },
    {
      path: baseURL() + "/Reports/TotalPerformance",
      name: "total_performance-report",
      component: TotalPerformanceReport,
      meta: reportsMeta,
    },
    {
      path: baseURL() + "/Reports/AgentsPerformance",
      name: "agents-performance-report",
      component: AgentPerformanceReport,
      meta: reportsMeta,
    },
    {
      path: baseURL() + "/Reports/AgentCalls",
      name: "agent-calls-report",
      component: AgentCallsReport,
      meta: { requireAuth: true, permission: "B" },
    },
    {
      path: baseURL() + "/Reports/AgentStatus",
      name: "agent-status-report",
      component: AgentStatusReport,
      meta: { requireAuth: true, permission: "B" },
    },
    {
      path: baseURL() + "/Reports/SystemInbound",
      name: "system-inbound-report",
      component: SystemInboundReport,
      meta: reportsMeta,
    },
    {
      path: baseURL() + "/Reports/LeavingRequest",
      name: "agent-leaving-detail-report",
      component: AgentLeavingDetailReport,
      meta: reportsMeta,
    },
    {
      path: baseURL() + "/Reports/LeavingRequestSum",
      name: "agent-leaving-summary-report",
      component: AgentLeavingSummaryReport,
      meta: reportsMeta,
    },
    {
      path: baseURL() + "/Reports/LoginHistory",
      name: "agent-login-detail-report",
      component: AgentLoginDetailReport,
      meta: reportsMeta,
    },
    {
      path: baseURL() + "/Reports/LoginHistorySum",
      name: "agent-login-summary-report",
      component: AgentLoginSummaryReport,
      meta: reportsMeta,
    },
    {
      path: baseURL() + "/Reports/UserStatus",
      name: "agent-status-detail-report",
      component: AgentStatusDetailReport,
      meta: reportsMeta,
    },
    {
      path: baseURL() + "/Reports/AgentsKPI",
      name: "agent-kpis-report",
      component: AgentKPIReport,
      meta: reportsMeta,
    },
    {
      path: baseURL() + "/Reports/CallAnalysis",
      name: "call-analysis",
      component: CallAnalysisDetails,
      meta: aiReportsMeta,
    },
    {
      path: baseURL() + "/Reports/VoiceCDR",
      name: "voice-cdr-details",
      component: VoiceCDRDetails,
      meta: aiReportsMeta,
    },
    {
      path: baseURL() + "/Reports/ChatTranscript",
      name: "chat-transcript-details",
      component: AIChatDetails,
      meta: aiReportsMeta,
    },

    {
      path: baseURL() + "/Reports/UserStatusSum",
      name: "agent-status-summary-report",
      component: AgentStatusSummaryReport,
      meta: reportsMeta,
    },
    {
      path: baseURL() + "/kpi-management",
      redirect: { name: "settings-kpi" },
    },
    {
      path: baseURL() + "/Settings",
      component: () => import("./pages/settings/SettingsConsole.vue"),
      meta: { requireAuth: true, permission: "J" },
      // Land on the first panel this user is allowed to open (an admin with
      // only the users permission must not be bounced into a "J" panel).
      redirect: () => {
        const permissions = store.getters.permissions || [];
        for (const section of SETTINGS_SECTIONS) {
          for (const item of section.items) {
            if (permissions.includes(item.permission)) {
              return { name: "settings-" + item.key };
            }
          }
        }
        return { name: "settings-queues" };
      },
      children: [
        {
          path: "queues/manage",
          name: "settings-queues",
          component: () => import("./pages/admin/skills/ManageSkill.vue"),
          meta: { requireAuth: true, permission: "J" },
        },
        {
          path: "queues/:queue",
          name: "settings-queue-config",
          component: () => import("./pages/admin/skills/QueueConfig.vue"),
          props: true,
          meta: { requireAuth: true, permission: "J" },
        },
        {
          path: "routing/vip",
          name: "settings-vip",
          component: () =>
            import("./features/smart-routing/components/NumberListManager.vue"),
          props: { listType: LIST_TYPES.VIP },
          meta: { requireAuth: true, permission: "AD" },
        },
        {
          path: "routing/blacklist",
          name: "settings-blacklist",
          component: () =>
            import("./features/smart-routing/components/NumberListManager.vue"),
          props: { listType: LIST_TYPES.BLACKLIST },
          meta: { requireAuth: true, permission: "AD" },
        },
        {
          path: "call-handling/reasons",
          name: "settings-reasons",
          component: () =>
            import("./pages/admin/calls/reasons/ReasonsManagement.vue"),
          meta: { requireAuth: true, permission: "J" },
        },
        {
          path: "call-handling/crm",
          name: "settings-crm",
          component: () =>
            import("./pages/admin/calls/crm/CRMManagement.vue"),
          meta: { requireAuth: true, permission: "J" },
        },
        {
          path: "call-handling/rating",
          name: "settings-rating",
          component: () =>
            import("./pages/admin/calls/rating/CallRating.vue"),
          meta: { requireAuth: true, permission: "J" },
        },
        {
          path: "call-handling/call-settings",
          name: "settings-call-settings",
          component: () => import("./pages/admin/skills/AcwManagement.vue"),
          meta: { requireAuth: true, permission: "J" },
        },
        {
          path: "performance/kpi",
          name: "settings-kpi",
          component: () => import("./pages/admin/kpi/KpiManagement.vue"),
          meta: { requireAuth: true, permission: "J" },
        },
        {
          path: "workforce/users",
          name: "settings-users",
          component: () =>
            import("./pages/admin/users/UsersManagement.vue"),
          meta: { requireAuth: true, permission: "I" },
        },
        {
          path: "workforce/rest-periods",
          name: "settings-rest-periods",
          component: () => import("./pages/admin/skills/SkillsPeriod.vue"),
          meta: { requireAuth: true, permission: "J" },
        },
      ],
    },
    {
      path: baseURL() + "/Campaigns",
      name: "campaigns-management",
      component: () => import("./features/campaigns/CampaignsPage.vue"),
      meta: { requireAuth: true, permission: "AD" },
    },
    {
      path: baseURL() + "/Campaigns/new",
      name: "campaign-new",
      component: () => import("./features/campaigns/CampaignFormPage.vue"),
      meta: { requireAuth: true, permission: "AD" },
    },
    {
      path: baseURL() + "/Campaigns/sounds",
      name: "campaign-sounds",
      component: () => import("./features/campaigns/SoundsPage.vue"),
      meta: { requireAuth: true, permission: "AD" },
    },
    {
      path: baseURL() + "/Campaigns/:id/edit",
      name: "campaign-edit",
      component: () => import("./features/campaigns/CampaignFormPage.vue"),
      meta: { requireAuth: true, permission: "AD" },
    },
    {
      path: baseURL() + "/Campaigns/:id/reports",
      name: "campaign-reports",
      component: () => import("./features/campaigns/CampaignReportsPage.vue"),
      meta: { requireAuth: true, permission: "AD" },
    },
    {
      path: baseURL() + "/IVR",
      name: "ivr-management",
      component: () => import("./features/ivr/IvrTreesPage.vue"),
      meta: { requireAuth: true, permission: "AD" },
    },
    {
      path: baseURL() + "/IVR/new",
      name: "ivr-new",
      component: () => import("./features/ivr/IvrBuilderPage.vue"),
      meta: { requireAuth: true, permission: "AD" },
    },
    // Must stay ahead of /IVR/:id — otherwise "sounds" is read as a flow id.
    {
      path: baseURL() + "/IVR/sounds",
      name: "ivr-sounds",
      component: () => import("./features/ivr/IvrSoundsPage.vue"),
      meta: { requireAuth: true, permission: "AD" },
    },
    {
      path: baseURL() + "/IVR/:id",
      name: "ivr-edit",
      component: () => import("./features/ivr/IvrBuilderPage.vue"),
      meta: { requireAuth: true, permission: "AD" },
    },
    // {
    //   path: baseURL() + "/SMS",
    //   name: "sms-page",
    //   component: SMSPage,
    //   meta: { requireAuth: true, permission: "P" },
    // },

    {
      path: baseURL() + "/:notFound(.*)",
      component: NotFound,
      meta: { requireAuth: true },
    },
  ],
});

router.beforeEach(function (to, _, next) {
  // Web only: if the URL tenant differs from the session tenant, log out.
  // On desktop the tenant is fixed (config.json) and the app loads from a
  // file:// URL with no tenant, so this check would misfire on every
  // navigation and loop — skip it entirely.
  if (!isDesktop) {
    const storedDomain = localStorage.getItem("domain");
    if (storedDomain) {
      const urlDomain = getTenant();
      if (urlDomain && urlDomain !== storedDomain) {
        localStorage.removeItem("token");
        localStorage.removeItem("uname");
        localStorage.removeItem("expiration");
        localStorage.removeItem("status");
        localStorage.removeItem("domain");
        window.location.href = "/" + urlDomain + "/Login";
        return;
      }
    }
  }

  if (
    store.getters.isAuthenticated &&
    to.meta.requireAuth &&
    !hasPermisson(to.meta.permission)
  ) {
    if (store.getters.domain) {
      store.dispatch("logout");
      next(false);
    } else {
      store.dispatch("autoLogin").then(
        (response) => {
          if (response.success) {
            if (
              store.getters.isAuthenticated &&
              to.meta.requireAuth &&
              !hasPermisson(to.meta.permission)
            ) {
              console.log(2);
              store.dispatch("logout");
              next(false);
            } else {
              next();
            }
          } else {
            store.dispatch("SetMessageDialog", {
              show: true,
              message: response.message,
            });
            next(false);
          }
        },
        (error) => {
          let message = error.message;
          if (error.response) {
            message = error.response.data.message;
          }
          store.dispatch("SetMessageDialog", { show: true, message: message });
          next(false);
        }
      );
    }
  } else if (store.getters.isAuthenticated && !to.meta.requireAuth) {
    // Log out wallboard users instead of redirecting
    if (store.getters.type === 5) {
      store.dispatch("logout");
      next(false);
    } else {
      next(baseURL() + "/");
    }
  } else if (!store.getters.isAuthenticated && to.meta.requireAuth) {
    console.log(4);
    next(baseURL() + "/Login");
  } else {
    next();
  }
});

function hasPermisson(permission) {
  var permissions = store.getters.permissions;
  return permissions.includes(permission);
}

export default router;
